import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { authFetch } from '../../utils/authFetch';

interface Agent {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'AGENT';
  isActive: boolean;
  lastLoginAt?: string;
  assignedChats: number;
}

const AgentManagement: React.FC = () => {
  const { user } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);

  // Load agents from database
  useEffect(() => {
    const loadAgents = async () => {
      try {
        // authFetch automatically prepends /api
        const response = await authFetch('/users');
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            const agentsWithChatCount = data.data.map((agent: any) => ({
              ...agent,
              assignedChats: agent._count?.assignedChats || 0
            }));
            setAgents(agentsWithChatCount);
          }
        }
      } catch (error) {
        console.error('Failed to load agents:', error);
      } finally {
        setLoading(false);
      }
    };

    loadAgents();
  }, []);

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState<Agent | null>(null);
  const [newAgent, setNewAgent] = useState({
    email: '',
    name: '',
    password: '',
    role: 'AGENT' as 'ADMIN' | 'AGENT'
  });
  const [editAgent, setEditAgent] = useState({
    email: '',
    name: '',
    password: '',
    role: 'AGENT' as 'ADMIN' | 'AGENT'
  });

  const handleAddAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // authFetch automatically prepends /api
      const response = await authFetch('/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: newAgent.email,
          name: newAgent.name,
          password: newAgent.password,
          role: newAgent.role
        }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          const newAgentWithCount = {
            ...data.user,
            assignedChats: 0
          };
          setAgents([...agents, newAgentWithCount]);
          setNewAgent({ email: '', name: '', password: '', role: 'AGENT' });
          setShowAddForm(false);
          alert('Agent added successfully!');
        } else {
          throw new Error(data.error || 'Registration failed');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create agent');
      }
    } catch (error) {
      console.error('Add agent error:', error);
      alert('Failed to add agent: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleEditAgent = (agent: Agent) => {
    setEditingAgent(agent);
    setEditAgent({
      email: agent.email,
      name: agent.name,
      password: '',
      role: agent.role
    });
  };

  const handleUpdateAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAgent) return;
    
    try {
      // authFetch automatically prepends /api
      const response = await authFetch(`/users/${editingAgent.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: editAgent.name,
          email: editAgent.email,
          role: editAgent.role
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Agent updated:', data);
        
        // Update local state
        const updatedAgents = agents.map(agent => 
          agent.id === editingAgent.id 
            ? { 
                ...agent, 
                name: editAgent.name,
                email: editAgent.email,
                role: editAgent.role
              }
            : agent
        );
        setAgents(updatedAgents);
        setEditingAgent(null);
        alert('Agent updated successfully!');
      } else {
        throw new Error('Failed to update agent');
      }
    } catch (error) {
      console.error('Update agent error:', error);
      alert('Failed to update agent');
    }
  };

  const handleDeleteAgent = async (agentId: string) => {
    if (!window.confirm('Are you sure you want to delete this agent? This action cannot be undone.')) {
      return;
    }
    
    try {
      // authFetch automatically prepends /api
      const response = await authFetch(`/users/${agentId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Agent deleted:', data);
        
        // Remove from local state
        setAgents(agents.filter(agent => agent.id !== agentId));
        setEditingAgent(null);
        alert('Agent deleted successfully!');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete agent');
      }
    } catch (error) {
      console.error('Delete agent error:', error);
      alert('Failed to delete agent: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const toggleAgentStatus = async (agentId: string) => {
    try {
      const agent = agents.find(a => a.id === agentId);
      if (!agent) return;

      // authFetch automatically prepends /api
      const response = await authFetch(`/users/${agentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          isActive: !agent.isActive
        }),
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Agent status updated:', data);
        
        // Update local state
        const updatedAgents = agents.map(a => 
          a.id === agentId 
            ? { ...a, isActive: !a.isActive }
            : a
        );
        setAgents(updatedAgents);
        
        alert(`Agent ${agent.isActive ? 'deactivated' : 'activated'} successfully!`);
      } else {
        throw new Error('Failed to update agent status');
      }
    } catch (error) {
      console.error('Toggle agent status error:', error);
      alert('Failed to update agent status');
    }
  };

  const formatLastLogin = (dateString?: string) => {
    if (!dateString) return 'Never';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
    return `${Math.floor(diffInHours / 24)}d ago`;
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h4>Access Denied</h4>
        <p>Only administrators can manage agents.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div className="loading-spinner" style={{ margin: '0 auto 12px' }}></div>
        <p>Loading agents...</p>
      </div>
    );
  }

  return (
    <div className="agent-management">
      <div className="agent-header">
        <h2>Agent Management</h2>
        <button 
          className="add-agent-button"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          + Add Agent
        </button>
      </div>

      {showAddForm && (
        <div className="add-agent-form">
          <form onSubmit={handleAddAgent}>
            <div className="form-row">
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newAgent.name}
                  onChange={(e) => setNewAgent({...newAgent, name: e.target.value})}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={newAgent.email}
                  onChange={(e) => setNewAgent({...newAgent, email: e.target.value})}
                  className="form-input"
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  value={newAgent.password}
                  onChange={(e) => setNewAgent({...newAgent, password: e.target.value})}
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={newAgent.role}
                  onChange={(e) => setNewAgent({...newAgent, role: e.target.value as 'ADMIN' | 'AGENT'})}
                  className="form-input"
                >
                  <option value="AGENT">Agent</option>
                  <option value="ADMIN">Administrator</option>
                </select>
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)} className="cancel-button">
                Cancel
              </button>
              <button type="submit" className="submit-button">
                Add Agent
              </button>
            </div>
          </form>
        </div>
      )}

      {editingAgent && (
        <div className="assignment-modal-overlay" onClick={() => setEditingAgent(null)}>
          <div className="assignment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="assignment-header">
              <h4>Edit Agent: {editingAgent.name}</h4>
              <button onClick={() => setEditingAgent(null)} className="close-button">×</button>
            </div>
            
            <div className="assignment-content">
              <form onSubmit={handleUpdateAgent} className="edit-agent-form">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editAgent.name}
                    onChange={(e) => setEditAgent({...editAgent, name: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    value={editAgent.email}
                    onChange={(e) => setEditAgent({...editAgent, email: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
                
                <div className="form-group">
                  <label>New Password (leave blank to keep current)</label>
                  <input
                    type="password"
                    value={editAgent.password}
                    onChange={(e) => setEditAgent({...editAgent, password: e.target.value})}
                    className="form-input"
                    placeholder="Enter new password or leave blank"
                  />
                </div>
                
                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={editAgent.role}
                    onChange={(e) => setEditAgent({...editAgent, role: e.target.value as 'ADMIN' | 'AGENT'})}
                    className="form-input"
                  >
                    <option value="AGENT">Agent</option>
                    <option value="ADMIN">Administrator</option>
                  </select>
                </div>
              </form>
            </div>

            <div className="assignment-actions">
              <button onClick={() => setEditingAgent(null)} className="cancel-button">
                Cancel
              </button>
              <button 
                onClick={() => handleDeleteAgent(editingAgent.id)}
                className="danger-button"
              >
                Delete Agent
              </button>
              <button onClick={handleUpdateAgent} className="assign-button">
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="agents-table">
        <div className="table-header">
          <div className="table-cell">Agent</div>
          <div className="table-cell">Role</div>
          <div className="table-cell">Status</div>
          <div className="table-cell">Last Login</div>
          <div className="table-cell">Assigned Chats</div>
          <div className="table-cell">Actions</div>
        </div>
        
        {agents.map((agent) => (
          <div key={agent.id} className="table-row">
            <div className="table-cell">
              <div className="agent-info">
                <div className="agent-avatar">
                  {agent.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                </div>
                <div>
                  <div className="agent-name">{agent.name}</div>
                  <div className="agent-email">{agent.email}</div>
                </div>
              </div>
            </div>
            <div className="table-cell">
              <span className={`role-badge role-${agent.role.toLowerCase()}`}>
                {agent.role}
              </span>
            </div>
            <div className="table-cell">
              <span className={`status-indicator ${agent.isActive ? 'online' : 'offline'}`}>
                ● {agent.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="table-cell">
              {formatLastLogin(agent.lastLoginAt)}
            </div>
            <div className="table-cell">
              {agent.assignedChats} chats
            </div>
            <div className="table-cell">
              <button
                onClick={() => handleEditAgent(agent)}
                className="action-btn edit"
              >
                Edit
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AgentManagement;